import { ApolloClient } from '@apollo/client';
import { Box, Button, FormControl, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import { Field, Form, Formik, FormikValues } from 'formik';
import { TextField } from 'formik-mui';
import { useEffect, useState } from 'react';
import { EMAIL_REGEX } from '../util';
import { useOrgService } from '../lib/verid-api/use-org-service';
import { useAuthService } from '../lib/verid-api';
import { ClientLogger } from '../lib/client-logger';
import { useErrorHandling } from '../lib/hooks/useErrorHandling';

interface IStyle {
  formContainer?: any;
  itemHolder?: any;
  form?: any;
}

interface IProps {
  orgId: string | null;
  authConfigId: string | null;
  style?: IStyle;
  theme?: any;
  onClickSignUpRedirect: () => void;
  onSignInSuccess?: () => void;
}

interface IValues {
  email: string;
  password: string;
}

const DEBUG = false;

export function OrgLogin(props: IProps) {
  const { orgId, authConfigId, style, theme, onClickSignUpRedirect, onSignInSuccess } = props;
  const errorHandler = useErrorHandling('OrgLogin');
  const cssClasses = {
    formContainer: {
      width: '400px',
      maxWidth: '90vw',
      position: 'relative',
      minHeight: '20vh',
      padding: '24px',
      [theme.breakpoints.down('sm')]: {
        margin: '4px auto',
        padding: '48px 16px 16px 16px',
      },
    },
    itemHolder: {
      marginBottom: '16px',
      width: '100%',
    },
    formField: {
      marginBottom: '12px',
    },
    orgSignIn: {
      marginTop: '12px',
    },
    signUpLink: {
      textDecoration: 'none',
      marginLeft: '6px',
    },
    btnPrimary: {
      padding: `16px 8px`,
    },
  };

  const [showPassword, setShowPassword] = useState(false);
  const [orgAuth, setOrgAuth] = useState<any | null>(null);
  const orgService = useOrgService();
  const authService = useAuthService();

  useEffect(() => {
    if (orgId) {
      orgService
        .org(orgId)
        .then((response) => {
          const org = response.data?.org;

          if (org && org.authConfig && org.authConfig.length > 0) {
            const authConfig = org.authConfig.find((item: any) => item.id === authConfigId);
            setOrgAuth(authConfig);
          }
        })
        .catch((error) => {
          console.error('error: ', error);
        });
    }
  }, []);

  const handleSubmit = async (
    values: FormikValues,
    {
      setSubmitting,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
    }
  ) => {
    DEBUG && ClientLogger.debug('OrgLogin', 'handleSubmit', { values });
    setSubmitting(true);
    const resp = await authService.login({ email: values.email, password: values.password });
    DEBUG && ClientLogger.debug('OrgLogin', 'handleSubmit response', { resp });
    if (resp.data?.login) {
      onSignInSuccess && onSignInSuccess();
    } else {
      errorHandler.handleErrors({ graphQLErrors: resp.errors, error: 'Error loggin in' });
    }
  };

  return (
    <Grid container flexDirection="column" justifyContent="center">
      <Paper sx={style?.formContainer || cssClasses.formContainer}>
        <Grid container justifyContent="center">
          <Grid sx={style?.itemHolder || cssClasses.itemHolder}>
            <Grid container direction="column" justifyContent="center">
              <Box mt={1} mb={3}>
                <Typography variant="h3">{orgAuth?.signInPage?.title || 'Sign In'}</Typography>
              </Box>
              <Formik
                initialValues={{
                  email: '',
                  password: '',
                }}
                validate={(values) => {
                  const errors: Partial<IValues> = {};
                  if (!values.email) {
                    errors.email = 'Required';
                  } else if (!EMAIL_REGEX.test(values.email)) {
                    errors.email = 'Invalid email address';
                  }
                  return errors;
                }}
                onSubmit={async (values, { setSubmitting }) => {
                  DEBUG && ClientLogger.debug('OrgLogin', 'Formik onSubmit', { values });

                  await handleSubmit(values, { setSubmitting });
                }}
              >
                {({ submitForm, isSubmitting }) => (
                  <Form className={style?.form}>
                    {orgAuth?.signInPage?.formTitle && (
                      <Typography variant="h4" align="center">
                        {orgAuth?.signInPage?.formTitle}
                      </Typography>
                    )}
                    <Grid container direction="column" justifyContent="center" alignItems="center">
                      <FormControl fullWidth required variant="filled">
                        <Field
                          component={TextField}
                          name="email"
                          type="text"
                          label={orgAuth?.signInPage?.inputEmailLabel || 'Email Address'}
                          data-test="input-email"
                          variant="outlined"
                          sx={cssClasses.formField}
                        />

                        <Field
                          component={TextField}
                          type={showPassword ? 'text' : 'password'}
                          label={orgAuth?.signInPage?.inputEmailPassword || 'Password'}
                          name="password"
                          variant="outlined"
                          data-test="input-password"
                          sx={cssClasses.formField}
                          InputProps={{
                            endAdornment: (
                              <Typography onClick={() => (showPassword ? setShowPassword(false) : setShowPassword(true))} variant="caption">
                                {showPassword ? 'Hide' : 'Show'}
                              </Typography>
                            ),
                          }}
                        />
                      </FormControl>
                      {isSubmitting && <LinearProgress />}
                      <Typography variant="caption" color="textSecondary" component="p">
                        {orgAuth?.signInPage?.resetPasswordSection?.textPreLink || 'Need Help Signing In?'}{' '}
                        <a href={orgAuth?.signInPage?.resetPasswordSection?.link}>
                          {orgAuth?.signInPage?.resetPasswordSection?.textLink || 'Set or Change your Healix Password'}
                        </a>
                      </Typography>

                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={submitForm}
                        fullWidth
                        data-test="button-sign-in"
                        sx={cssClasses.btnPrimary}
                      >
                        {orgAuth?.signInPage?.buttonSignIn || 'Sign In'}
                      </Button>
                    </Grid>
                  </Form>
                )}
              </Formik>
              <Box mt={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    console.log('Magic Link clicked');
                  }}
                  fullWidth
                  data-test="button-sign-in"
                  sx={cssClasses.btnPrimary}
                >
                  {orgAuth?.signInPage?.buttonMagicLink || 'Sign In with Magic Link'}
                </Button>
              </Box>

              <Box mt={2} mb={3} textAlign="center">
                <Typography variant="subtitle2">
                  {orgAuth?.signInPage?.signUpRedirectText || "Don't have an account? "}
                  <Box component="span" sx={cssClasses.signUpLink} onClick={onClickSignUpRedirect}>
                    {orgAuth?.signInPage?.signUpRedirectButton || 'Sign up'}
                  </Box>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
